/**
 * PaymentHistoryPage.js
 * Standalone page listing all the user's magazine payment history.
 * Covers both yearly subscriptions and per-copy orders, sorted by date descending.
 */
import React, { useState, useEffect } from 'react';
import { History, Loader, RefreshCw } from 'lucide-react';
import { vendorService } from '../../services/api';

const PaymentHistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await vendorService.getMagazinePaymentHistory();
      if (res.success && res.data?.history) {
        setHistory(res.data.history);
      } else {
        setError(res.error || 'Failed to load payment history.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">Completed</span>;
      case 'PENDING':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">Pending</span>;
      case 'FAILED':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold">Failed</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">{status}</span>;
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <History size={22} className="text-[#2ab391]" />
            History of Payments
          </h1>
          <p className="text-gray-500 text-sm mt-1">All your magazine subscription and order payments</p>
        </div>
        <button
          onClick={fetchHistory}
          disabled={loading}
          className="flex items-center gap-2 text-sm text-[#2ab391] border border-[#2ab391] px-3 py-2 rounded-lg hover:bg-[#e0f7f8] transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader size={28} className="animate-spin text-[#2ab391]" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-5 text-red-700 text-sm">
          {error}
        </div>
      ) : history.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-gray-50 rounded-xl border border-gray-100 text-center">
          <History size={36} className="text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No payment history yet</p>
          <p className="text-gray-400 text-sm mt-1">Your magazine payments will appear here after purchase.</p>
        </div>
      ) : (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Transactions', value: history.length },
              { label: 'Completed', value: history.filter(h => h.status === 'COMPLETED').length, color: 'text-emerald-600' },
              { label: 'Pending', value: history.filter(h => h.status === 'PENDING').length, color: 'text-amber-600' },
              {
                label: 'Total Paid',
                value: `₹${history.filter(h => h.status === 'COMPLETED').reduce((s, h) => s + h.amount, 0).toLocaleString('en-IN')}`,
                color: 'text-[#2ab391]'
              },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm text-center">
                <p className="text-xs text-gray-400 mb-1 font-medium">{label}</p>
                <p className={`text-xl font-bold ${color || 'text-gray-800'}`}>{value}</p>
              </div>
            ))}
          </div>

          {/* Payments Table */}
          <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-sm bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">#</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Date &amp; Time</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Edition</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Qty</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Order ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {history.map((item, idx) => (
                  <tr key={`${item.type}-${item.id}`} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-400 text-xs">{idx + 1}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className="text-gray-700 font-medium">{formatDate(item.created_at)}</p>
                      <p className="text-gray-400 text-xs">{formatTime(item.created_at)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                        item.type === 'Yearly Subscription'
                          ? 'bg-[#e0f7f8] text-[#2ab391]'
                          : 'bg-indigo-50 text-indigo-600'
                      }`}>
                        {item.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{item.edition || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 text-center">{item.quantity ?? '—'}</td>
                    <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap">
                      ₹{item.amount?.toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(item.status)}</td>
                    <td className="px-4 py-3 text-xs text-gray-400 font-mono max-w-[140px] truncate" title={item.razorpay_order_id}>
                      {item.razorpay_order_id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentHistoryPage;
